import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, Bot, User } from 'lucide-react';
import './ChatAssistant.css';

const ChatAssistant = ({ user }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messageCount, setMessageCount] = useState(0);
    const [pendingMessages, setPendingMessages] = useState([]); // Store messages locally
    const [sessionSent, setSessionSent] = useState(false); // Track if this session was sent
    const MAX_MESSAGES = 3;
    const [messages, setMessages] = useState([
        {
            type: 'bot',
            text: `Hello ${user?.full_name?.split(' ')[0] || 'there'}! 👋 How are you today?\nTell us what you like 😊 or don't like 😔\nYour comments help us improve. You are most welcome! 🙏`,
            time: new Date()
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (isOpen) {
            scrollToBottom();
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [messages, isOpen]);

    // Function to send all pending messages as one batch
    const sendAllMessages = useCallback(async () => {
        if (pendingMessages.length === 0 || sessionSent) return;

        try {
            const token = localStorage.getItem('token');
            // Combine all messages into one
            const combinedMessage = pendingMessages.join('\n---\n');

            await fetch('/api/feedback', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ message: combinedMessage })
            });

            console.log('[Feedback] Sent batch of', pendingMessages.length, 'messages');
            setSessionSent(true);
        } catch (error) {
            console.error('Failed to send feedback batch:', error);
        }
    }, [pendingMessages, sessionSent]);

    // Send messages when session completes (3 messages reached)
    useEffect(() => {
        if (messageCount >= MAX_MESSAGES && pendingMessages.length > 0 && !sessionSent) {
            sendAllMessages();
        }
    }, [messageCount, pendingMessages, sessionSent, sendAllMessages]);

    // Send messages when chat is closed (if there are pending messages)
    const prevIsOpen = useRef(isOpen);
    useEffect(() => {
        // Chat was just closed and has pending unsent messages
        if (!isOpen && prevIsOpen.current && pendingMessages.length > 0 && !sessionSent) {
            sendAllMessages();
        }

        // Chat reopened after session completed - reset everything
        if (isOpen && !prevIsOpen.current && sessionSent) {
            setMessages([
                {
                    type: 'bot',
                    text: `Hello ${user?.full_name?.split(' ')[0] || 'there'}! 👋 How are you today?\nTell us what you like 😊 or don't like 😔\nYour comments help us improve. You are most welcome! 🙏`,
                    time: new Date()
                }
            ]);
            setMessageCount(0);
            setPendingMessages([]);
            setSessionSent(false);
        }

        prevIsOpen.current = isOpen;
    }, [isOpen, user?.full_name, pendingMessages, sessionSent, sendAllMessages]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!inputValue.trim() || isLoading || messageCount >= MAX_MESSAGES) return;

        const userMsg = inputValue.trim();

        // Add user message to chat
        setMessages(prev => [...prev, { type: 'user', text: userMsg, time: new Date() }]);
        setInputValue('');

        // Store message locally (don't send yet)
        setPendingMessages(prev => [...prev, userMsg]);

        const newCount = messageCount + 1;
        setMessageCount(newCount);

        // Show response based on count
        setTimeout(() => {
            let botResponse;
            if (newCount === 1) {
                botResponse = `Thanks! 😊 Is there anything else you want to add?`;
            } else if (newCount === 2) {
                botResponse = `Got it! 👍 Have some more?`;
            } else {
                botResponse = `Thanks! 😉 Your comments are valuable to us!`;
            }

            setMessages(prev => [...prev, {
                type: 'bot',
                text: botResponse,
                time: new Date()
            }]);
            scrollToBottom();
        }, 300);

        scrollToBottom();
    };

    const toggleChat = () => {
        setIsOpen(!isOpen);
    };

    return (
        <div className="chat-assistant-container">
            {isOpen && (
                <div className="chat-window">
                    <div className="chat-header">
                        <div className="chat-header-icon">
                            <Bot size={20} color="white" />
                        </div>
                        <div className="chat-header-info">
                            <h3>Feedback</h3>
                        </div>
                        <button className="chat-close-button" onClick={toggleChat} title="Close">
                            ✕
                        </button>
                    </div>

                    <div className="chat-messages">
                        {messages.map((msg, index) => (
                            <div key={index} className={`message ${msg.type}`}>
                                <div className="message-avatar">
                                    {msg.type === 'bot' ? <Bot size={16} /> : <User size={16} />}
                                </div>
                                <div className="message-content">
                                    {msg.text}
                                </div>
                            </div>
                        ))}
                        {isLoading && (
                            <div className="message bot">
                                <div className="message-avatar">
                                    <Bot size={16} />
                                </div>
                                <div className="message-content">
                                    <div className="typing-indicator">
                                        <div className="typing-dot"></div>
                                        <div className="typing-dot"></div>
                                        <div className="typing-dot"></div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <div className="chat-input-area">
                        <form className="chat-input-form" onSubmit={handleSubmit}>
                            <input
                                ref={inputRef}
                                type="text"
                                placeholder={messageCount >= MAX_MESSAGES ? "Session complete 🙏" : "Your feedback..."}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                disabled={isLoading || messageCount >= MAX_MESSAGES}
                            />
                            <button type="submit" className="chat-send-button" disabled={isLoading || !inputValue.trim() || messageCount >= MAX_MESSAGES} title="Send">
                                ➤
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {!isOpen && (
                <button className="chat-bubble-button" onClick={toggleChat}>
                    <MessageCircle size={24} />
                </button>
            )}
        </div>
    );
};

export default ChatAssistant;
